import React from 'react';

/**
 * Premium HTML/CSS/SVG mockup of the Relix Delivery Review for the Solutions
 * explorer panel.
 *
 * Cropped to one message (RLX-FEAT-008): the health score and the decision
 * queue — "what is the state, and what do I fix first". Priority rows,
 * callouts, findings, and the trend chart were removed so the remaining
 * content stays readable at panel size.
 *
 * Status copy mirrors the real product band for a 64/100 score
 * (`overallHeadlineFromScore`): "Review Needed" — acceptable overall, but
 * several areas need attention before release.
 *
 * Marketing-only, decorative; no interactivity, no data fetching, no app routes.
 * The score ring draws once on mount (gated by `prefers-reduced-motion`).
 */

const DECISION_QUEUE: readonly {
  num: string;
  title: string;
  hint: string;
}[] = [
  {
    num: '1',
    title: 'Review commitment baseline changes',
    hint: 'Scope changed after baseline.',
  },
  {
    num: '2',
    title: 'Check Delivery Confidence signals',
    hint: 'Planning assumptions may no longer match reality.',
  },
  {
    num: '3',
    title: 'Review Quality hotspots',
    hint: 'Bugs are concentrated in key areas.',
  },
  {
    num: '4',
    title: 'Recalculate Capacity impact',
    hint: 'Absences reduced available working days.',
  },
] as const;

const RING_RADIUS = 70;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const SCORE_PCT = 64;
const SCORE_DASH = (SCORE_PCT / 100) * RING_CIRCUMFERENCE;

export function DeliveryReviewHeroMockup(): React.ReactElement {
  return (
    <div
      className="dr-hero-mockup product-window"
      role="img"
      aria-label="Relix Delivery Review preview showing a 64 out of 100 delivery health score marked Review Needed, with a prioritized decision queue."
    >
      <span className="product-window__chip" aria-hidden="true">
        Delivery Review · ALFA
      </span>
      <span
        className="product-window__chip product-window__chip--right product-window__chip--accent"
        aria-hidden="true"
      >
        North Star
      </span>

      <div className="dr-hero-mockup__viewport" aria-hidden="true">
        <div className="dr-hero-mockup__row dr-hero-mockup__row--top">
          <section className="dr-hero-status">
            <div className="dr-hero-status__ring-wrap">
              <svg
                className="dr-hero-status__ring"
                viewBox="0 0 180 180"
                role="presentation"
                focusable="false"
              >
                <defs>
                  <linearGradient id="drHeroRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#facc15" />
                    <stop offset="55%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#fb7185" />
                  </linearGradient>
                </defs>
                <circle
                  cx="90"
                  cy="90"
                  r={RING_RADIUS}
                  stroke="rgba(148, 163, 184, 0.18)"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  className="dr-hero-status__ring-fill"
                  cx="90"
                  cy="90"
                  r={RING_RADIUS}
                  stroke="url(#drHeroRingGrad)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  fill="none"
                  transform="rotate(-90 90 90)"
                  strokeDasharray={`${SCORE_DASH} ${RING_CIRCUMFERENCE}`}
                  style={
                    {
                      ['--dr-ring-target' as string]: `${SCORE_DASH}`,
                      ['--dr-ring-full' as string]: `${RING_CIRCUMFERENCE}`,
                    } as React.CSSProperties
                  }
                />
              </svg>
              <div className="dr-hero-status__ring-center">
                <span className="dr-hero-status__score">
                  64<span className="dr-hero-status__score-unit">/100</span>
                </span>
                <span className="dr-hero-status__score-label">Delivery health score</span>
              </div>
            </div>

            <div className="dr-hero-status__lead">
              <span className="dr-hero-status__badge">
                <span className="dr-hero-status__badge-dot" />
                Review Needed
              </span>
              <h3 className="dr-hero-status__title">Delivery health needs review.</h3>
            </div>

            <div className="dr-hero-status__details">
              <p className="dr-hero-status__sub">
                Acceptable overall — but several areas need attention before release.
              </p>
              <p className="dr-hero-status__context">
                ALFA · Release 4.08.05 · Sprint ALFA3 · May 17, 2026
              </p>
            </div>
          </section>

          <section className="dr-hero-queue" aria-label="Decision queue">
            <header className="dr-hero-queue__head">
              <span className="dr-hero-queue__eyebrow">Decision queue</span>
              <span className="dr-hero-queue__count">4 open</span>
            </header>
            <ul className="dr-hero-queue__list">
              {DECISION_QUEUE.map((item) => (
                <li key={item.num} className="dr-hero-queue__row">
                  <span className="dr-hero-queue__rank">{item.num}</span>
                  <span className="dr-hero-queue__body">
                    <span className="dr-hero-queue__title">{item.title}</span>
                    <span className="dr-hero-queue__hint">{item.hint}</span>
                  </span>
                  <span className="dr-hero-queue__open" aria-hidden="true">
                    Open
                    <svg viewBox="0 0 12 12" width="10" height="10">
                      <path
                        d="M3 1.5 L8.5 6 L3 10.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
